import styled from '@emotion/styled';
import React, { Key, useCallback, useEffect, useMemo } from 'react';
import { semantic_spacing } from '../../foundation';
import { buildCSSWithLength, CSSValueWithLength } from '../../styles';
import { useProviderConfig } from '../provider';
import { TableCell } from './TableCell';
import { TableRow } from './TableRow';
import {
  DataTableProps,
  DataTableRenderExpandedRow,
  SelectRowAction,
  DataTableRowType,
  ExpandedRowAction,
  DataTableRowSelectionRecord,
} from './types';
import { getFlattenColumns } from './utils';
import { State } from '../../container/state';
import { DataTableRow } from './DataTableRow';
import { VirtualItem } from '@tanstack/react-virtual';

interface DataTableRowsProps<DataType>
  extends Pick<
    DataTableProps<DataType>,
    | 'columns'
    | 'rows'
    | 'emptyContent'
    | 'emptyPadding'
    | 'loading'
    | 'selectableRows'
    | 'clickableRows'
    | 'hasCellHoverStyle'
    | 'hasCellDisabledStyle'
    | 'resizable'
  > {
  tableContainerAttribute: number;
  totalColumnCount: number;
  allSelected?: boolean;
  selectedRows?: Map<Key, DataTableRowSelectionRecord<DataType>>;
  expandedRows?: Key[];
  hasExpandButton: boolean;
  renderExpandedRow?: DataTableRenderExpandedRow<DataType>;
  shouldRenderRowDivider?: (row: DataType, index: number) => boolean;
  rowColor?: (row: DataTableRowType<DataType>, index: number) => (string | undefined) | string;
  getRowKey: (row: DataType, index: number) => Key;
  getRowSelectionKey: (row: DataType, index: number) => Key;
  onSelectedRow?: (action: SelectRowAction<DataType>) => void;
  onExpandedRow?: (action: ExpandedRowAction<DataType>) => void;
  virtualizedItems?: VirtualItem[];
}

export const DataTableRows = <DataType extends object>({
  columns,
  rows,
  loading,
  selectableRows,
  clickableRows,
  selectedRows,
  expandedRows,
  emptyContent,
  emptyPadding,
  hasCellHoverStyle,
  hasCellDisabledStyle,
  tableContainerAttribute,
  totalColumnCount,
  resizable,
  hasExpandButton,
  renderExpandedRow,
  shouldRenderRowDivider,
  onSelectedRow,
  onExpandedRow,
  rowColor,
  getRowKey,
  getRowSelectionKey,
  virtualizedItems,
}: DataTableRowsProps<DataType>) => {
  const { locale } = useProviderConfig();
  const computedFlattenColumns = useMemo(() => getFlattenColumns({ columns }), [columns]);

  const handleSelectRow = useCallback(
    (row: DataTableRowType<DataType>, index: number, selected: boolean) => {
      onSelectedRow?.({ type: 'SELECT_ROW', rows, row, index, selected });
    },
    [onSelectedRow, rows],
  );

  const handleExpandRow = useCallback(
    (row: DataTableRowType<DataType>, index: number, expanded: boolean) => {
      onExpandedRow?.({ type: 'EXPAND_ROW', row, index, expanded });
    },
    [onExpandedRow],
  );

  const EmptyRow = () => (
    <TableRow empty>
      <TableCell className='pds-table-cell pds-table-cell-empty' colSpan={totalColumnCount} empty lastRow>
        <EmptyContent
          maxWidth={tableContainerAttribute > 0 ? tableContainerAttribute : undefined}
          paddingBlock={emptyPadding ?? semantic_spacing.empty_state.padding.desktop}
        >
          <State
            status={loading ? 'loading' : 'empty'}
            description={loading ? '' : emptyContent || locale?.Table.noDataText}
          />
        </EmptyContent>
      </TableCell>
    </TableRow>
  );

  const items = useMemo(
    () =>
      virtualizedItems
        ? virtualizedItems.map((virtualItem) => {
            return {
              row: rows[virtualItem.index],
              rowIndex: virtualItem.index,
            };
          })
        : rows.map((row, index) => {
            return {
              row,
              rowIndex: index,
            };
          }),
    [virtualizedItems, rows],
  );

  useEffect(() => {
    const isUnique =
      new Set(
        items.map((item, index) => {
          const rowType = item.row as DataTableRowType<DataType>;
          return getRowKey(rowType, index);
        }),
      ).size === items.length;

    if (!isUnique) {
      throw new Error('getRowKey 고유한 값을 반환해야합니다.');
    }
  }, [items, getRowKey]);

  if (loading || rows.length === 0) {
    return <EmptyRow />;
  }

  return (
    <>
      {items.map(({ row, rowIndex }) => {
        const rowType = row as DataTableRowType<DataType>;
        const selectionKey = (selectableRows || renderExpandedRow) && getRowSelectionKey(rowType, rowIndex);
        const extractedKey = getRowKey(rowType, rowIndex);
        const selected = selectionKey && selectedRows?.has(selectionKey);
        const expanded = selectionKey && expandedRows?.includes(selectionKey);

        return (
          <DataTableRow
            key={extractedKey}
            rows={rows}
            columns={computedFlattenColumns}
            index={rowIndex}
            row={rowType}
            lastRow={rowIndex === rows.length - 1}
            selected={Boolean(selected)}
            expanded={Boolean(expanded)}
            rowColor={typeof rowColor === 'string' ? rowColor : rowColor?.(rowType, rowIndex)}
            selectionKey={selectionKey}
            onExpandedRow={handleExpandRow}
            onSelectedRow={handleSelectRow}
            hasExpandButton={hasExpandButton}
            renderExpandedRow={renderExpandedRow}
            shouldRenderRowDivider={shouldRenderRowDivider}
            selectableRows={selectableRows}
            clickableRows={clickableRows}
            hasCellHoverStyle={hasCellHoverStyle}
            hasCellDisabledStyle={hasCellDisabledStyle}
            resizable={resizable}
          />
        );
      })}
    </>
  );
};

const EmptyContent = styled.div<{ maxWidth?: number; paddingBlock: CSSValueWithLength }>`
  position: sticky;
  left: 0;
  ${({ paddingBlock }) => buildCSSWithLength('padding-block', paddingBlock)};
  padding-inline: 0;
  width: 100%;
  text-align: center;
  ${({ maxWidth }) => typeof maxWidth === 'number' && buildCSSWithLength('max-width', maxWidth)};
`;
